/**
 * @file App.js
 * @description Root application component for CaraFin AI Customer Management.
 *              Sets up MSAL authentication, MUI theming (with runtime client config),
 *              and React Router with role-based protected routes.
 *
 *              Route groups:
 *              - Agent    : agentstatus, agentchat, agentcalls, agentoutgoing, agentvm, conversations
 *              - Admin    : Users, roles, permissions, skills, compliance
 *              - Campaign : campaign, inactivecampaigns, processes, upload-files, data-review
 *              - Customer : customers, Payment, inconv
 *              - Dashboard: activity, keyword, optin, lang, promises, conversationtime,
 *                           dpd, compliancestats, transfers, customerdash
 *              - IVR      : policy, queue, job, worker, assignment
 *              - Schedule : upschedule, upmessage, responses, edit-schedule/:campaignID
 *              - Insights : abandon, abandonevent, responserate, willing, emotions, polarity, score
 *              - Logs     : calllogs, integration
 *              - Landing  : home, agentm, adminm, customerm, dashboardm, datadm, ivrm, campaignm, mgrm
 *
 * @component App
 * @exports App - Root React component wrapping ThemeProvider and BrowserRouter.
 */
import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { getclientconfiguration } from "./services/api";
import { useIsAuthenticated } from "@azure/msal-react";
import ProtectedRoute from "./routes/ProtectedRoute.jsx";
import "./assets/scss/app.scss";
import Layout from "./components/LayoutComponent";
import ClientConfiguration from "./components/configuration";
import LiveChatRecords from "./components/livechat";
import LiveEmail from "./components/LiveEmail.jsx";
import MGRChatRecords from "./components/mgrchat";
import PowerBIManagerView from "./features/pbi/PowerBIReport.jsx";
import Campaigns from "./components/getcampaign";
import Schedule from "./features/schedule/Schedule.jsx";
import LoginPage from "./components/LoginPage";
import Users from "./features/adminModule/users/Users.jsx";
import Roles from "./features/adminModule/roles/Roles.jsx";
import Permissions from "./features/adminModule/permissions/Permissions.jsx";
import Skills from "./features/adminModule/skills/Skills.jsx";
import ManagePermissions from "./features/adminModule/permissions/ManagePermissions.jsx";
import Customers from "./features/customerModule/customers/Customers.jsx";
import DataReview from "./features/data-review/DataReview";
import AgentStatus from "./features/agentModule/status/AgentStatus.jsx";
import TextConversation from "./features/agentModule/text-conversation/TextConversation.jsx";
import EditConversation from "./features/agentModule/text-conversation/EditConversation.jsx";
import IncomingCalls from "./features/callsModule/incoming/IncomingCallLayout.jsx";
import InactiveCampaigns from "./components/getinactivecampaigns";
import EmailConversation from "./features/agentModule/emails/EmailConversation";
import EditEmailConversation from "./features/agentModule/emails/EditEmailConversation";
import IvrPolicy from "./features/ivrModule/policy/Policy.jsx";
import IvrQueue from "./features/ivrModule/queue/Queue.jsx";
import IvrJob from "./features/ivrModule/job/Job.jsx";
import IvrWorker from "./features/ivrModule/worker/Worker.jsx";
import IvrAssignment from "./features/ivrModule/assignment/Assignment.jsx";
import DataUpload from "./features/dataModule/dataUpload/DataUpload";
import ReminderExclusionUpload from "./features/dataModule/dataUpload/Reminderexclusionupload.jsx";
import AgentLandingPage from "./features/landingpage/landingpageAgent.js";
import AdminLandingPage from "./features/landingpage/landingpageAdmin.js";
import CampaignLandingPage from "./features/landingpage/landingpageCampaign.js";
import CustomerLandingPage from "./features/landingpage/landingpageCustomer.js";
import DashboardLandingPage from "./features/landingpage/landingpageDashboard.js";
import DataLandingPage from "./features/landingpage/landingpageData.js";
import IvrLandingPage from "./features/landingpage/landingpageIvr.js";
import MgrLandingPage from "./features/landingpage/landingpageMgr.js";
import HomeLandingPage from "./features/landingpage/landpageHomepage.js";
import OutgoingChats from "./features/chatModule/Chats.jsx";
import CallLogs from "./features/logsModule/callLogs.jsx";
import IntegrationCenterPage from "./features/integrationModule/integration.jsx";
import VoiceMailLayout from "./features/callsModule/voicemail/VoicemailTabLayout";
import OutgoingTabLayout from "./features/callsModule/outgoing/OutgoingTabLayout";
import UpcomingSchedules from "./features/scheduleModule/UpcomingSchedules.jsx";
import Responses from "./features/scheduleModule/Responses.jsx";
import UpcomingMessages from "./features/scheduleModule/UpcomingMessages.jsx";
import CustomersConv from "./features/customerConvModule/CustomersConv.jsx";
import NoAccess from "./components/NoAccess.jsx";
import Compliance from "./features/adminModule/compliance/Compliance.jsx";
import Payment from "./features/customerModule/payments/Payments.jsx";
import Payments from "./features/dashboard/payments/Payments.jsx";
import KeywordSearch from "./features/dashboard/keyword/KeywordSearch.jsx";
import Optin from "./features/dashboard/optin/Optin.jsx";
import Language from "./features/dashboard/language/Language.jsx";
import Promises from "./features/dashboard/promises/Promises.jsx";
import ConversationTime from "./features/dashboard/conversationtime/ConversationTime.jsx";
import DPD from "./features/dashboard/dpd/DPD.jsx";
import ComplianceStats from "./features/dashboard/complianceStats/ComplianceStats.jsx";
import Transfers from "./features/dashboard/transfers/Transfers.jsx";
import AbondonResp from "./features/insightModule/abandonResponse/AbondonResp.jsx";
import AbandonEvent from "./features/insightModule/abandonEvent/AbandonEvent.jsx";
import ResponseRate from "./features/insightModule/responseRate/ResponseRate.jsx";
import CustomersDash from "./features/dashboard/customersDash/CustomerDash.jsx";
import Willing from "./features/insightModule/willing/Willing.jsx";
import Emotion from "./features/insightModule/emotion/Emotion.jsx";
import Polarity from "./features/insightModule/polarity/Polarity.jsx";
import Score from "./features/insightModule/score/Score.jsx";
import Processes from "./features/campaignModule/processes.jsx";
import PtpResponses from "./features/scheduleModule/ptpresponses.jsx";
import Reminders from "./features/scheduleModule/reminders.jsx";
import ForecastManagerView from "./features/forecast/Forecastmanagerview.jsx";
import DailyAccountIds from "./features/dashboard/dailyaccounts/Dailyaccountids.jsx";
import EventActivity from "./features/dashboard/dpdactivity/eventactivity.jsx"
import DpdDayActivity from "./features/dashboard/dpdheatmap/Dpddayactivity.jsx"
import ReportConfig from "./features/report/ReportConfig.jsx"
import UploadConfig from "./features/uploadAutomationModule/UploadAutomationConfig.jsx"
import OpsView from "./features/ops/OpsView";


const App = () => {
    const [allowedPaths, setAllowedPaths] = useState([]);
    const [companyName, setCompanyName] = useState("CaraFin AI");

    const setFont = (f) => setTheme((t) => t);
    const setPrimaryColor = (c) => setTheme((t) => t);
    const setSecondaryColor = (c) => setTheme((t) => t);

    const [theme, setTheme] = useState(
        createTheme({
            palette: {
                primary: { main: "#286EFA" },
                secondary: { main: "#edf2ff" },
                info: { main: "#666", light: "#f0f0f0" },
                text: { primary: "#363636" },
            },
            typography: {
                fontFamily: "Poppins, Arial",
                button: { textTransform: "none" },
            },
            components: {
                MuiTextField: {
                    styleOverrides: {
                        root: {
                            "& .MuiInputBase-input.Mui-readOnly": {
                                backgroundColor: "#FAFAFA",
                                color: "#000000",
                            },
                        },
                    },
                },
                MuiAccordion: {
                    styleOverrides: {
                        root: {
                            boxShadow: "0 0 10px #ccc",
                            borderRadius: "4px",
                            "&::before": { content: "none" },
                            "&:not(:last-child)": { marginBottom: "16px" },
                        },
                    },
                },
            },
        })
    );

    const isAuthenticated = useIsAuthenticated();

    useEffect(() => {
        if (!isAuthenticated) return;

        const fetchConfiguration = async () => {
            try {
                const data = await getclientconfiguration();
                setCompanyName(data?.companyName || "CaraFin AI");
                setPrimaryColor(data?.primaryColor || "#286EFA");
                setSecondaryColor(data?.secondaryColor || "#edf2ff");
                setFont(data?.font || "Poppins, Arial");
                setTheme((prevTheme) =>
                    createTheme({
                        ...prevTheme,
                        palette: {
                            primary: { main: data?.primaryColor || "#286EFA" },
                            secondary: { main: data?.secondaryColor || "#edf2ff" },
                            info: { main: "#666" },
                        },
                        typography: {
                            fontFamily: data?.font || "Arial",
                            button: { textTransform: "none" },
                        },
                    })
                );
            } catch (error) {
                console.error("Error fetching configuration:", error);
            }
        };
        fetchConfiguration();
    }, [isAuthenticated]);

    return (
        <ThemeProvider theme={theme}>
            <Router>
                <Routes>
                    {/* Public route - always accessible */}
                    <Route path="/login" element={<LoginPage />} />

                    {/* Authenticated routes */}
                    {isAuthenticated ? (
                        <Route
                            path="/"
                            element={
                                <Layout
                                    companyName={companyName}
                                    setAllowedPaths={setAllowedPaths}
                                />
                            }
                        >
                            <Route index element={<HomeLandingPage />} />
                            <Route path="home" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <HomeLandingPage />
                                </ProtectedRoute>
                            } />
                            <Route path="customers" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <Customers />
                                </ProtectedRoute>
                            } />
                             <Route path="models" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <ForecastManagerView />
                                </ProtectedRoute>
                            } />
                            <Route path="upload-files" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <DataUpload />
                                </ProtectedRoute>
                            } />
                            <Route path="exclusion" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <ReminderExclusionUpload />
                                </ProtectedRoute>
                            } />
                            <Route path="data-review" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <DataReview />
                                </ProtectedRoute>
                            } />
                            <Route path="conversations" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <TextConversation />
                                </ProtectedRoute>
                            } />
                            <Route path="opsview" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <OpsView />
                                </ProtectedRoute>
                            } />
                            <Route path="reports" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <ReportConfig />
                                </ProtectedRoute>
                            } />
                            <Route path="Payment" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <Payment />
                                </ProtectedRoute>
                            } />
                            <Route path="uploadconfig" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <UploadConfig />
                                </ProtectedRoute>
                            } />
                            <Route path="livechat" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <LiveChatRecords />
                                </ProtectedRoute>
                            } />
                            <Route path="liveemail" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <LiveEmail />
                                </ProtectedRoute>
                            } />
                            <Route path="DpdDayActivity" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <DpdDayActivity />
                                </ProtectedRoute>
                            } />
                            <Route path="campaign" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <Campaigns />
                                </ProtectedRoute>
                            } />
                            <Route path="agentstatus" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <AgentStatus />
                                </ProtectedRoute>
                            } />
                            <Route path="processes" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <Processes />
                                </ProtectedRoute>
                            } />
                            <Route path="dpdactivity" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <EventActivity />
                                </ProtectedRoute>
                            } />
                            <Route path="dailyaccounts" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <DailyAccountIds />
                                </ProtectedRoute>
                            } />
                            <Route path="configuration" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <ClientConfiguration />
                                </ProtectedRoute>
                            } />
                            <Route path="mystats" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <PowerBIManagerView />
                                </ProtectedRoute>
                            } />
                            <Route path="teamstats" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <PowerBIManagerView />
                                </ProtectedRoute>
                            } />
                            <Route path="activity" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <Payments />
                                </ProtectedRoute>
                            } />
                            <Route path="inactivecampaigns" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <InactiveCampaigns />
                                </ProtectedRoute>
                            } />
                            <Route path="agentchat" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <OutgoingChats />
                                </ProtectedRoute>
                            } />
                            <Route path="agentcalls" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <IncomingCalls />
                                </ProtectedRoute>
                            } />
                            <Route path="agentoutgoing" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <OutgoingTabLayout />
                                </ProtectedRoute>
                            } />
                            <Route path="mgr" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <MgrLandingPage />
                                </ProtectedRoute>
                            } />
                            <Route path="mgrtexts" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <div>In Progress</div>
                                </ProtectedRoute>
                            } />
                            <Route path="mgrchat" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <MGRChatRecords />
                                </ProtectedRoute>
                            } />
                            <Route path="roles" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <Roles />
                                </ProtectedRoute>
                            } />
                            <Route path="permissions" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <Permissions />
                                </ProtectedRoute>
                            } />
                            <Route path="skills" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <Skills />
                                </ProtectedRoute>
                            } />
                             <Route path="reminders" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <Reminders />
                                </ProtectedRoute>
                            } />
                            <Route path="Users" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <Users />
                                </ProtectedRoute>
                            } />
                            <Route path="emailrecords" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <EmailConversation />
                                </ProtectedRoute>
                            } />
                            <Route path="policy" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <IvrPolicy />
                                </ProtectedRoute>
                            } />
                            <Route path="queue" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <IvrQueue />
                                </ProtectedRoute>
                            } />
                            <Route path="job" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <IvrJob />
                                </ProtectedRoute>
                            } />
                            <Route path="worker" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <IvrWorker />
                                </ProtectedRoute>
                            } />
                            <Route path="assignment" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <IvrAssignment />
                                </ProtectedRoute>
                            } />
                            <Route path="upschedule" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <UpcomingSchedules />
                                </ProtectedRoute>
                            } />
                            <Route path="upmessage" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <UpcomingMessages />
                                </ProtectedRoute>
                            } />
                            <Route path="responses" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <Responses />
                                </ProtectedRoute> 
                            } />
                             <Route path="PtpResponses" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <PtpResponses />
                                </ProtectedRoute> 
                            } />
                            <Route path="compliancestats" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <ComplianceStats />
                                </ProtectedRoute>
                            } />
                            <Route path="keyword" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <KeywordSearch />
                                </ProtectedRoute>
                            } />
                            <Route path="optin" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <Optin />
                                </ProtectedRoute>
                            } />
                            <Route path="lang" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <Language />
                                </ProtectedRoute>
                            } />
                            <Route path="promises" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <Promises />
                                </ProtectedRoute>
                            } />
                            <Route path="conversationtime" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <ConversationTime />
                                </ProtectedRoute>
                            } />
                            <Route path="dpd" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <DPD />
                                </ProtectedRoute>
                            } />
                            <Route path="transfers" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <Transfers />
                                </ProtectedRoute>
                            } />
                            <Route path="abandon" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <AbondonResp />
                                </ProtectedRoute>
                            } />
                            <Route path="abandonevent" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <AbandonEvent />
                                </ProtectedRoute>
                            } />
                            <Route path="responserate" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <ResponseRate />
                                </ProtectedRoute>
                            } />
                            <Route path="willing" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <Willing />
                                </ProtectedRoute>
                            } />
                            <Route path="emotions" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <Emotion />
                                </ProtectedRoute>
                            } />
                            <Route path="polarity" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <Polarity />
                                </ProtectedRoute>
                            } />
                            <Route path="score" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <Score />
                                </ProtectedRoute>
                            } />
                            <Route path="customerdash" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <CustomersDash />
                                </ProtectedRoute>
                            } />
                            <Route path="inconv" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <CustomersConv />
                                </ProtectedRoute>
                            } />
                            <Route path="calllogs" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <CallLogs />
                                </ProtectedRoute>
                            } />
                            <Route path="compliance" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <Compliance />
                                </ProtectedRoute>
                            } />
                            <Route path="integration" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <IntegrationCenterPage />
                                </ProtectedRoute>
                            } />
                            <Route path="agentvm" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <VoiceMailLayout />
                                </ProtectedRoute>
                            } />
                            <Route path="ivrm" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <IvrLandingPage />
                                </ProtectedRoute>
                            } />
                            <Route path="mgrm" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <MgrLandingPage />
                                </ProtectedRoute>
                            } />
                            <Route path="campaignm" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <CampaignLandingPage />
                                </ProtectedRoute>
                            } />
                            <Route path="agentm" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <AgentLandingPage />
                                </ProtectedRoute>
                            } />
                            <Route path="adminm" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <AdminLandingPage />
                                </ProtectedRoute>
                            } />
                            <Route path="customerm" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <CustomerLandingPage />
                                </ProtectedRoute>
                            } />
                            <Route path="dashboardm" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <DashboardLandingPage />
                                </ProtectedRoute>
                            } />
                            <Route path="datadm" element={
                                <ProtectedRoute allowedPaths={allowedPaths}>
                                    <DataLandingPage />
                                </ProtectedRoute>
                            } />
                            <Route
                                path="/permissions/manage-permissions/:roleId"
                                element={<ManagePermissions />}
                            />
                            <Route
                                path="/customer-email-records/:customerdataID"
                                element={
                                    <ProtectedRoute allowedPaths={allowedPaths}>
                                        <EditEmailConversation />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/customer-chat-records/:customerdataID"
                                element={
                                    <ProtectedRoute allowedPaths={allowedPaths}>
                                        <EditConversation />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/edit-schedule/:campaignID"
                                element={
                                    <ProtectedRoute allowedPaths={allowedPaths}>
                                        <Schedule />
                                    </ProtectedRoute>
                                }
                            />
                            <Route path="no-access" element={<NoAccess />} />

                            {/* Catch all — redirect unknown paths to home */}
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Route>
                    ) : (
                        /* Not authenticated — send everything to login */
                        <Route path="*" element={<Navigate to="/login" replace />} />
                    )}
                </Routes>
            </Router>
        </ThemeProvider>
    );
};

export default App;